<template>
    <div v-if="trailerStore.selectedTrailer?.description" class="trailer-description-wrapper">
        <h2 class="trailer-description-title">Opis lawety</h2>
        <div class="trailer-description">
            {{ trailerStore.selectedTrailer.description }}
        </div>
    </div>

    <div v-if="trailerStore.selectedTrailer?.technical_drawing" class="technical-drawing-wrapper">
        <button class="btn-show-drawing" @click="openImageZoom(trailerStore.selectedTrailer.technical_drawing_url)">
            Pokaż rysunek techniczny
        </button>
    </div>

    <div class="options">
        <div class="section-nav">
            <div class="section-nav-text">
                {{ hoverText || (activeSection + 1) + ' / ' + options.length + ' ' + options[activeSection] }}
            </div>

            <div class="section-nav-buttons">
                <div v-for="(option, index) in options" :key="index" class="nav-button"
                    @mouseenter="setHoverText(index)" @mouseleave="resetHoverText" @click="scrollToSection(index)">
                    <div class="nav-square" :class="{
                        active: hoverIndex !== null ? index <= hoverIndex : index <= activeSection
                    }"></div>
                </div>
            </div>
        </div>

        <div ref="sectionsContainer">
            <div v-for="(option, index) in options" :key="index" :ref="'section' + index" class="content-section">
                <h1>{{ option }}</h1>

                <div class="option-section">
                    <!-- <h2>Zaznacz dodatki</h2> -->
                    <div class="option-group">
                        <div class="checkbox-option" v-for="addon in groupedAddons[option]" :key="addon.id">
                            <input type="checkbox" :checked="isSelected(addon)"
                                :key="addon.id + '-' + isSelected(addon)" @change="toggleAddon(addon)" /> <img
                                v-if="addon.image_url" :src="addon.image_url" :alt="addon.desc" class="addon-thumbnail"
                                @click="openImageZoom(addon.image_url)" />

                            <div class="checkbox-content">
                                <div class="addon-title">
                                    <b>{{ addon.code }}</b> - {{ addon.desc }}<template v-if="addon.is_default"> <span
                                            class="default-tag"> (domyślny)</span></template>
                                </div>
                                <div class="addon-price">{{ trailerStore.formatPrice(addon.price) }} zł</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <teleport to="body">
        <transition name="fade">
            <div v-if="zoomedImage" class="zoom-modal" @click.self="closeImageZoom">
                <div class="zoomed-image-container">
                    <img :src="zoomedImage" alt="Powiększone zdjęcie" class="zoomed-img" />
                    <button class="close-btn" @click="closeImageZoom">×</button>
                </div>
            </div>
        </transition>
    </teleport>

</template>

<script>
import { useTrailerStore } from '@/stores/trailerStore';
import { getConfigFromStorage } from '@/services/configStorage';

export default {
    data() {
        return {
            activeSection: 0,
            hoverText: '',
            hoverIndex: null,
            hasUnsavedChanges: false,
            zoomedImage: null
        };
    },
    mounted() {
        this.setupIntersectionObserver();

        const saved = getConfigFromStorage();

        if (!this.trailerStore.selectedTrailer && (!saved || !saved.trailer)) {
            this.$router.push('/');
            return;
        }

        if (saved && saved.trailer && !this.trailerStore.selectedTrailer) {
            this.trailerStore.selectTrailer(saved.trailer);
            this.trailerStore.updateSelectedAddons(saved.addons);
        }

        if (!this.selectedAddons.length) {
            const defaults = this.trailerStore.moreDetailedTrailerAddons.filter(a => a.is_default);
            this.selectedAddons = defaults;
        }

        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    },
    computed: {
        trailerStore() {
            return useTrailerStore();
        },
        groupedAddons() {
            const groups = {};
            for (const addon of this.trailerStore.moreDetailedTrailerAddons) {
                if (!groups[addon.group]) {
                    groups[addon.group] = [];
                }
                groups[addon.group].push(addon);
            }
            return groups;
        },
        selectedAddons: {
            get() {
                return this.trailerStore.selectedTrailerAddons;
            },
            set(val) {
                this.trailerStore.updateSelectedAddons(val);
            }
        },
        options() {
            return Object.keys(this.groupedAddons);
        }
    },
    watch: {
        groupedAddons: {
            immediate: true,
            handler(newVal) {
                const keys = Object.keys(newVal);
                if (keys.length > 0) {
                    this.$emit('options-ready', keys);
                }
            }
        }
    },
    methods: {
        setupIntersectionObserver() {
            this.observer = new IntersectionObserver(
                (entries) => {
                    const visible = entries
                        .filter(entry => entry.isIntersecting)
                        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                    if (visible.length > 0) {
                        const topEntry = visible[0];
                        const index = this.options.findIndex(
                            (_, i) => this.$refs['section' + i][0] === topEntry.target
                        );

                        if (index !== -1 && this.activeSection !== index) {
                            this.activeSection = index;
                            this.$emit('section-visible', index);
                        }
                    }
                },
                {
                    threshold: 0.3,
                    rootMargin: '-30% 0px -40% 0px'
                }
            );

            this.$nextTick(() => {
                this.options.forEach((_, index) => {
                    if (this.$refs['section' + index]) {
                        this.observer.observe(this.$refs['section' + index][0]);
                    }
                });
            });
        },
        setHoverText(index) {
            this.hoverText = `Przejdź do ${this.options[index]}`;
            this.hoverIndex = index;
        },
        resetHoverText() {
            this.hoverText = '';
            this.hoverIndex = null;
        },
        scrollToSection(index) {
            if (this.$refs['section' + index]) {
                this.$refs['section' + index][0].scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        },
        beforeUnloadHandler(event) {
            if (this.hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = '';
            }
        },
        openImageZoom(url) {
            this.zoomedImage = url;
        },
        closeImageZoom() {
            this.zoomedImage = null;
        },
        isSelected(addon) {
            return this.selectedAddons.some(a => a.id === addon.id);
        },

        toggleAddon(addon) {
            const isAlreadySelected = this.isSelected(addon);
            let updated = [...this.selectedAddons];

            if (isAlreadySelected) {
                updated = updated.filter(a => a.id !== addon.id);
            } else {
                updated.push(addon);

                const excludesThis = this.parseExcludes(addon.excludes);
                updated = updated.filter(a => !excludesThis.includes(a.code));

                updated = updated.filter(a => {
                    const excludes = this.parseExcludes(a.excludes);
                    return !excludes.includes(addon.code);
                });
            }

            this.selectedAddons = updated;
        },

        parseExcludes(raw) {
            try {
                if (typeof raw === 'string') return JSON.parse(raw);
                if (Array.isArray(raw)) return raw;
            } catch (e) {
                console.warn('Błędny JSON w excludes:', raw);
            }
            return [];
        }

    }
};
</script>

<style scoped lang="scss">
.trailer-description-wrapper {
    padding: 20px;
    background-color: $color-grey-light;
    border-left: 4px solid $color-primary;
    cursor: default;
    background-image: url('~@/assets/images/tlo_strony.png');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 50% 70%;
}

.trailer-description-title {
    font-size: 18px;
    font-weight: 600;
    color: $color-text;
    margin-bottom: 12px;
}

.trailer-description {
    font-size: 15px;
    line-height: 1.6;
    color: $color-text-light;
    text-align: justify;
}

.options {
    padding: 10px;
    background-color: $color-white;
    padding-bottom: 50px;

    @media (max-width: 768px) {
        padding-bottom: 250px;
    }
}

.section-nav {
    position: sticky;
    top: 0;
    background-color: $color-white;
    padding: 25px;
    border-bottom: 1px solid $color-grey;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.section-nav-text {
    font-size: medium;
    font-weight: 500;
    margin-bottom: 10px;
    color: $color-text;
    height: 20px;
}

.section-nav-buttons {
    display: flex;
    gap: 6px;
}

.nav-button {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.nav-square {
    width: 20px;
    height: 5px;
    border-radius: 15px;
    background-color: $color-grey;
    transition: all 0.3s ease;

    &.active {
        background-color: $color-primary;
        transform: scale(1.1);
    }
}

.content-section {
    padding: 30px 20px;
    margin-bottom: 20px;
    border-bottom: 1px solid $color-grey;
    scroll-margin-top: 100px;
}

h1 {
    margin-top: 0;
    color: $color-text;
    font-size: 20px;
    margin-bottom: 16px;
    font-weight: 600;
}

h2 {
    font-size: 18px;
    margin-bottom: 12px;
    font-weight: 500;
}

p {
    color: $color-text-light;
    line-height: 1.5;
    margin-bottom: 0;
    font-size: 15px;
}

.option-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.option-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.radio-option,
.checkbox-option {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    color: $color-text;
    padding: 4px 0;
    line-height: 1.4;

    input[type='radio'],
    input[type='checkbox'] {
        transform: scale(1.3);
        accent-color: $color-primary;
        cursor: pointer;
    }
}

.checkbox-content {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
}

.addon-title {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: $color-text-light;

    .default-tag {
        color: $color-primary;
        font-weight: 500;
    }

    b {
        font-weight: 600;
        color: $color-text;
    }
}

.default-tag {
    color: $color-primary;
    font-size: 14px;
    display: inline;
    white-space: nowrap;
}

.addon-thumbnail {
    width: 40px;
    height: 40px;
    cursor: zoom-in;
    object-fit: contain;
}

.addon-price {
    font-size: 14px;
    color: $color-text-light;
}

.select-input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 2px solid $color-grey-dark;
    font-size: 16px;
    width: 100%;
    max-width: 300px;
    transition: border-color 0.2s ease;
    cursor: pointer;
    background-color: $color-white;

    &:hover {
        border-color: $color-primary;
    }

    &:focus {
        outline: none;
        border-color: $color-primary;
    }
}

.range-input {
    width: 100%;
    max-width: 300px;
    accent-color: $color-primary;
}

.zoom-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.zoomed-image-container {
    max-width: 85vw;
    max-height: 85vh;
    background-color: $color-white;
    border-radius: 8px;
    padding: 20px;
    position: relative;
}

.zoomed-img {
    width: 50vw;
    max-height: 50vh;
    object-fit: contain;
}

.close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background: transparent;
    border: none;
    color: $color-primary;
    font-size: 36px;
    cursor: pointer;
}

.technical-drawing-wrapper {
    padding: 0px 20px;
}

.btn-show-drawing {
    padding: 8px 14px;
    width: 100%;
    background: $color-primary;
    color: white;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-show-drawing:hover {
    background-color: $color-primary-dark;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media screen and (min-width: 1500px) {
    .nav-square {
        width: 28px;
    }
}
</style>